import React, { useState, useEffect } from 'react';
import FloatingLabel from 'react-bootstrap/FloatingLabel';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import '../styles/SectionConnexion.scss';
import envelope from '../assets/envelope.svg';
import google from '../assets/iconGoogle.svg';
import user from '../assets/user.svg';
import marqueur from '../assets/marqueur.svg';
import eye2 from '../assets/eye2.svg';
import useUserSession from './useUserSession';
import { saveSession } from './sessionManager';
import { useTranslation } from 'react-i18next';

function GroupingInscription({ handleClick, handleAuth, handleConnectWithGoole, googleError, setGoogleError }) {
  const [formData, setFormData] = useState({
    countryCity: '',
    name: '',
    email: '',
    password: ''
  });

  const [errors, setErrors] = useState({});
  const { setUser } = useUserSession();
  const [showError, setShowError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (showError) {
      const timer = setTimeout(() => setShowError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [showError]);

  useEffect(() => {
    if (googleError) {
      const timer = setTimeout(() => setGoogleError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [googleError, setGoogleError]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const nameRegex = /^(?! )[A-Za-zÀ-ÖØ-öø-ÿ- ]{4,}$/;
    const countryCityRegex = /^(?! )[A-Za-zÀ-ÖØ-öø-ÿ, -]{4,}$/;
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d\s]).{8,}$/;

    setFormData((prevData) => ({ ...prevData, [name]: value }));

    switch (name) {
      case 'email':
        if (emailRegex.test(value)) {
          if (!!errors[name]) {
            delete errors[name];
          }
        } else {
          errors[name] = t('auth.invalidEmail');
        }
        break;
      case 'name':
        if (nameRegex.test(value)) {
          if (!!errors[name]) {
            delete errors[name];
          }
        } else if (value.length < 4) {
          errors[name] = t('auth.nameMinChars');
        } else {
          errors[name] = t('auth.nameInvalidChars');
        }
        break;
      case 'countryCity':
        if (countryCityRegex.test(value)) {
          if (!!errors[name]) {
            delete errors[name];
          }
        } else if (value.length < 4) {
          errors[name] = t('auth.cityMinChars');
        } else {
          errors[name] = t('auth.cityInvalidChars');
        }
        break;
      case 'password':
        if (passwordRegex.test(value)) {
          if (!!errors[name]) {
            delete errors[name];
          }
        } else if (value.length < 8) {
          errors[name] = t('auth.passwordMinChars');
        } else {
          errors[name] = t('auth.passwordComplexity');
        }
        break;
      default:
        if (!!errors[name]) {
          delete errors[name];
        }
        break;
    }
    setErrors({ ...errors }); // Mettre à jour les erreurs
  };

  const validateForm = () => {
    let formErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const nameRegex = /^(?! )[A-Za-zÀ-ÖØ-öø-ÿ- ]{4,}$/;
    const countryCityRegex = /^(?! )[A-Za-zÀ-ÖØ-öø-ÿ, -]{4,}$/;
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d\s]).{8,}$/;

    if (!formData.countryCity) {
      formErrors.countryCity = t('auth.countryCityRequired');
    } else if (!countryCityRegex.test(formData.countryCity)) {
      if (formData.countryCity.length < 4) {
        formErrors.countryCity = t('auth.cityMinChars');
      } else {
        formErrors.countryCity = t('auth.cityInvalidChars');
      }
    }

    if (!formData.name) {
      formErrors.name = t('auth.fullNameRequired');
    } else if (!nameRegex.test(formData.name)) {
      if (formData.name.length < 4) {
        formErrors.name = t('auth.nameMinChars');
      } else {
        formErrors.name = t('auth.nameInvalidChars');
      }
    }

    if (!formData.email) {
      formErrors.email = t('auth.emailAddressRequired');
    } else if (!emailRegex.test(formData.email)) {
      formErrors.email = t('auth.invalidEmailAddress');
    }

    if (!formData.password) {
      formErrors.password = t('auth.passwordRequired2');
    } else if (!passwordRegex.test(formData.password)) {
      if (formData.password.length < 8) {
        formErrors.password = t('auth.passwordMinChars');
      } else {
        formErrors.password = t('auth.passwordComplexity');
      }
    }

    return formErrors;
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    const formErrors = validateForm();
    setErrors(formErrors);

    if (Object.keys(formErrors).length === 0) {
      setIsSubmitting(true);
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/user/register`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        });

        const result = await response.json();
        if (response.ok) {
          if (result.status === 0) {
            setShowError('');
            saveSession({ uid: result.user._id, email: result.user.email, name: result.user.name, token: result.token, state: 1 });
            setUser({ uid: result.user._id, email: result.user.email, name: result.user.name, token: result.token, state: 1 });
          } else {
            setShowError(result.message);
            setFormData({
              countryCity: '',
              name: '',
              email: '',
              password: ''
            });
          }
        }
      } catch (error) {
        setShowError(t('auth.networkError'));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div className='GroupingConnexion'>
      {(showError || googleError) && (
        <div className='GroupingConnexionErrors'>
          <span>{showError || googleError}</span>
          <button type='button' onClick={() => { setShowError(''); if (setGoogleError) setGoogleError(''); }}>✕</button>
        </div>
      )}
      <div>
        <Form className='modal-body-form modal-body-form-inscrip' onSubmit={handleSubmit}>
          <Form.Group className="mb-2" controlId="countryCity">
            <FloatingLabel controlId="floatingInputCountryCity" label={t('auth.countryCity')} className="mb-2">
              <span className='form-floating-blueMagic'></span>
              <Form.Control
                type="text"
                placeholder={t('auth.countryCity')}
                name="countryCity"
                value={formData.countryCity}
                onChange={handleInputChange}
                isInvalid={!!errors.countryCity}
              />
              <img src={marqueur} alt='marqueur' />
              <Form.Control.Feedback type="invalid">{errors.countryCity}</Form.Control.Feedback>
            </FloatingLabel>
          </Form.Group>
          <Form.Group className="mb-2" controlId="name">
            <FloatingLabel controlId="floatingInputName" label={t('auth.fullName')}>
              <span className='form-floating-blueMagic'></span>
              <Form.Control
                type="text"
                placeholder={t('auth.fullName')}
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                isInvalid={!!errors.name}
              />
              <img src={user} alt='user' />
              <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
            </FloatingLabel>
          </Form.Group>
          <Form.Group className="mb-2" controlId="email">
            <FloatingLabel controlId="floatingInputEmail" label={t('auth.email')} className="mb-2">
              <span className='form-floating-blueMagic'></span>
              <Form.Control
                type="email"
                placeholder={t('auth.email')}
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                isInvalid={!!errors.email}
              />
              <img src={envelope} alt='envelope' />
              <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
            </FloatingLabel>
          </Form.Group>
          <Form.Group className="mb-2" controlId="password">
            <FloatingLabel controlId="floatingInputPassword" label={t('auth.password')}>
              <span className='form-floating-blueMagic'></span>
              <Form.Control
                type={showPassword ? "text" : "password"}
                placeholder={t('auth.password')}
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                isInvalid={!!errors.password}
              />
              <img src={eye2} alt='eye2' onClick={() => setShowPassword(!showPassword)} style={{cursor: 'pointer'}} />
              <Form.Control.Feedback type="invalid">{errors.password}</Form.Control.Feedback>
            </FloatingLabel>
          </Form.Group>
          <Button className='btn_inscrip' type="submit" disabled={isSubmitting}>
            {!isSubmitting ? <span>{t('auth.createMyAccount')}</span> : <span className="spinner-border text-light" role="status"></span>}
          </Button>
        </Form>
      </div>
      <p>{t('auth.createAccountWith')}</p>
      <div className='reseauSociaux reseauSociaux_inscrip'>
        <div className='reseauSociaux1' onClick={handleConnectWithGoole}>
          <img src={google} alt='google' />
        </div>
      </div>
      <div className='queryInscription'>
        <span className='queryInscription1'>{t('auth.alreadyHaveAccount')}</span>
        <span className='queryInscription2' onClick={handleClick}>{t('auth.connectHere')}</span>
      </div>
    </div>
  );
}

export default GroupingInscription;
